import { ENV } from '@/lib/env';
import { tg } from '@/lib/telegram';

/** Зеркало бэкендового EventIngestService::ALLOWED_TYPES. */
export type EventType =
  | 'screen_view'
  | 'card_swipe'
  | 'tap'
  | 'scroll_end'
  | 'session_start'
  | 'session_end';

interface BufferedEvent {
  type: EventType;
  props?: Record<string, unknown>;
  event_at: number;
  app_version?: string;
  platform?: string;
}

export interface TrackerDeps {
  apiBase: string;
  getToken: () => string | null;
  getContext: () => { app_version?: string; platform?: string };
  optOut: boolean;
  now?: () => number;
  uuid?: () => string;
  fetchImpl?: typeof fetch;
  flushIntervalMs?: number;
  flushThreshold?: number;
  maxBuffer?: number;
}

export interface Tracker {
  start: () => void;
  stop: () => void;
  trackEvent: (type: EventType, props?: Record<string, unknown>) => void;
  flush: (keepalive?: boolean) => Promise<void>;
  setOptOut: (value: boolean) => void;
  getSessionId: () => string;
  /** test-only */
  __bufferLength: () => number;
}

export function createTracker(deps: TrackerDeps): Tracker {
  const now = deps.now ?? (() => Date.now());
  const uuid = deps.uuid ?? (() => crypto.randomUUID());
  const doFetch = deps.fetchImpl ?? fetch;
  const FLUSH_INTERVAL = deps.flushIntervalMs ?? 8000;
  const THRESHOLD = deps.flushThreshold ?? 50;
  const MAX_BUFFER = deps.maxBuffer ?? 200;

  let buffer: BufferedEvent[] = [];
  let sessionId = '';
  let optOut = deps.optOut;
  let timer: ReturnType<typeof setInterval> | null = null;
  let started = false;

  function trackEvent(type: EventType, props?: Record<string, unknown>): void {
    if (optOut) return;
    const ctx = deps.getContext();
    buffer.push({ type, props, event_at: now(), app_version: ctx.app_version, platform: ctx.platform });
    if (buffer.length > MAX_BUFFER) {
      buffer = buffer.slice(buffer.length - MAX_BUFFER); // дропаем старые
    }
    if (buffer.length >= THRESHOLD) {
      void flush();
    }
  }

  async function flush(keepalive = false): Promise<void> {
    if (optOut || sessionId === '' || buffer.length === 0) return;
    const token = deps.getToken();
    if (!token) return;
    const events = buffer;
    buffer = [];
    try {
      await doFetch(`${deps.apiBase}/api/v1/events/batch`, {
        method: 'POST',
        keepalive,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, events }),
      });
    } catch {
      // best-effort — событие потеряно, не ретраим
    }
  }

  function onVisibility(): void {
    if (document.visibilityState === 'hidden') void flush(true);
  }
  function onPageHide(): void {
    trackEvent('session_end');
    void flush(true);
  }

  function start(): void {
    if (started) return;
    started = true;
    sessionId = uuid();
    trackEvent('session_start');
    timer = setInterval(() => void flush(), FLUSH_INTERVAL);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
  }

  function stop(): void {
    if (timer) clearInterval(timer);
    timer = null;
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    started = false;
  }

  return {
    start,
    stop,
    trackEvent,
    flush,
    setOptOut: (v) => { optOut = v; },
    getSessionId: () => sessionId,
    __bufferLength: () => buffer.length,
  };
}

// Дефолтный синглтон с реальными зависимостями. Токен и opt-out
// проставляются из auth-стора через configureTracker() (Task F2).
let tokenGetter: () => string | null = () => null;

export function configureTracker(opts: { getToken: () => string | null; optOut: boolean }): void {
  tokenGetter = opts.getToken;
  tracker.setOptOut(opts.optOut);
}

export const tracker: Tracker = createTracker({
  apiBase: ENV.API_BASE_URL,
  getToken: () => tokenGetter(),
  getContext: () => { const w = tg(); return { app_version: w?.version, platform: w?.platform }; },
  optOut: false,
});

export const trackEvent = (type: EventType, props?: Record<string, unknown>) => tracker.trackEvent(type, props);
export const startAnalytics = () => tracker.start();
export const setAnalyticsOptOut = (v: boolean) => tracker.setOptOut(v);
